import { useMutation } from '@tanstack/react-query';
import {
  Action,
  Alert,
  Card,
  Combobox,
  Modal,
  PageStatus,
  useToast,
} from 'corso-admin-ui';
import { isTruthy } from 'corso-types';
import { type FormEventHandler, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { match, P } from 'ts-pattern';
import api from '~/api';
import { PricingSet } from '~/components/PricingSet';
import { useProtectSetById, useProtectSets } from '~/hooks/useProtectSets';
import {
  useRefetchStore,
  useStoreContext,
  useStoreId,
} from '~/providers/StoreProvider';
import { StorePage } from './StorePage';

function UpdatePricingSetModal({ onClose }: { onClose: () => void }) {
  const storeId = useStoreId();
  const store = useStoreContext();
  const refetch = useRefetchStore();
  const toast = useToast();
  const query = useProtectSets();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      shipProtectSetId: store.defaultProtectSetId
        ? `${store.defaultProtectSetId}`
        : '',
    },
  });

  const { mutate: updatePricingSet, isPending } = useMutation({
    mutationFn: (shipProtectSetId: number) =>
      api.store(storeId).updatePricingSet({ shipProtectSetId }),
    onSuccess: () => {
      onClose();
      refetch();
      toast.show('Updated pricing set');
    },
    onError: () => toast.show('Failed to update pricing set'),
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit(({ shipProtectSetId }) => {
      if (!shipProtectSetId) return;
      updatePricingSet(Number.parseInt(shipProtectSetId, 10));
    })(event).catch(console.error);
  };

  return (
    <Modal show onClose={onClose} title="Update Pricing Set">
      {match(query)
        .with({ status: 'pending' }, () => (
          <PageStatus.Loading title="Loading pricing sets" />
        ))
        .with({ status: 'error' }, () => (
          <PageStatus.Error
            title="Failed to load pricing sets"
            error={query.error}
          />
        ))
        .with({ status: 'success', data: P.select() }, (pricingSets) => {
          const options = pricingSets.map((set) => ({
            value: `${set.id}`,
            label: set.displayName || set.name,
            keywords: [set.name, set.displayName, set.code, `${set.id}`].filter(
              isTruthy,
            ),
          }));

          return (
            <form className="flex flex-col gap-6" onSubmit={submitHandler}>
              <Alert heading="Dangerous Action" variant="warning">
                Updating a pricing set comes with great responsibility, please
                use the power granted here wisely!
              </Alert>
              <Controller
                control={control}
                name="shipProtectSetId"
                render={({ field }) => (
                  <Combobox
                    label="Pricing Set"
                    placeholder="Search pricing sets by name, id, or code"
                    options={options}
                    value={field.value || undefined}
                    onChange={(v) => field.onChange(v ?? '')}
                  />
                )}
              />
              <div className="flex justify-end">
                <Action variant="primary" type="submit" loading={isPending}>
                  Update Pricing Set
                </Action>
              </div>
            </form>
          );
        })
        .exhaustive()}
    </Modal>
  );
}

export default function StorePricingSetPage() {
  const store = useStoreContext();
  const query = useProtectSetById(store.defaultProtectSetId ?? undefined);
  const [showUpdate, setShowUpdate] = useState(false);

  return (
    <StorePage
      title="Pricing set"
      primaryAction={{
        content: 'Update Pricing Set',
        onAction: () => setShowUpdate(true),
      }}
    >
      {store.defaultProtectSetId == null ? (
        <Card>
          <PageStatus
            icon="tag"
            title="No default pricing set"
            detail="This store does not have a default pricing set."
          />
        </Card>
      ) : (
        match(query)
          .with({ status: 'pending' }, () => (
            <PageStatus.Loading title="Loading pricing set" />
          ))
          .with({ status: 'error' }, () => (
            <PageStatus.Error
              title="Failed to load pricing set"
              error={query.error}
            />
          ))
          .with({ status: 'success', data: P.select() }, (detail) => (
            <PricingSet detail={detail} fromStore />
          ))
          .exhaustive()
      )}

      {showUpdate && (
        <UpdatePricingSetModal onClose={() => setShowUpdate(false)} />
      )}
    </StorePage>
  );
}
